import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, MapPin, Loader2 } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import { startCheckout } from "../../utils/checkout";

const money = (n) => `$${Number(n || 0) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2)}`;

// Top-ups: extra deals that never expire, and extra map pins by the month.
export const TopUpShelf = ({ addons = [], credits, canBuy, promo }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  // Pack names come from the backend in English; show the localised copy when we have it.
  const tr = (key, fallback) => {
    const out = t(key);
    return out === key ? fallback : out;
  };

  const creditPacks = addons.filter((a) => a.kind === "credits").sort((a, b) => a.amount - b.amount);
  const branchPacks = addons.filter((a) => a.kind === "branches").sort((a, b) => a.amount - b.amount);
  if (!creditPacks.length && !branchPacks.length) return null;

  const buy = async (id) => {
    if (!canBuy) { navigate("/login"); return; }
    setLoading(id);
    try { await startCheckout(id, { code: promo?.code }); }
    catch (e) { toast({ title: t("prc.checkoutFail"), description: e.response?.data?.detail || t("prc.tryAgain") }); setLoading(""); }
  };

  const Pack = ({ a, icon: Icon, per }) => (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      data-testid={`topup-${a.id}`}>
      <span className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center">
        <Icon size={18} className="text-[#16a34a]" />
      </span>
      <p className="font-bold text-foreground mt-3">{tr(`tu.${a.id}.label`, a.label)}</p>
      <p className="text-sm text-muted-foreground mt-1 flex-1">{tr(`tu.${a.id}.note`, a.note)}</p>
      <p className="text-2xl font-extrabold text-foreground mt-3">{money(a.amount)}
        {a.days ? <span className="text-sm font-semibold text-muted-foreground"> {t("tu.perMonth")}</span> : null}
      </p>
      {per && <p className="text-xs text-muted-foreground">{per}</p>}
      <button onClick={() => buy(a.id)} disabled={loading === a.id} data-testid={`topup-buy-${a.id}`}
        className="mt-4 w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2">
        {loading === a.id ? <Loader2 className="animate-spin" size={15} /> : null} {t("tu.buy")}
      </button>
    </div>
  );

  return (
    <section data-testid="topup-shelf">
      <h2 className="text-xl font-extrabold text-foreground">{t("tu.title")}</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        {t("tu.sub")}
        {credits ? ` ${t("tu.youHold", { n: credits.dealCredits || 0 })}` : ""}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {creditPacks.map((a) => (
          <Pack key={a.id} a={a} icon={Ticket}
            per={t("tu.each", { price: money(a.amount / (a.credits || 1)) })} />
        ))}
      </div>
      {branchPacks.length > 0 && (
        <>
          <h3 className="text-base font-bold text-foreground mt-6 mb-3">{t("tu.locationsTitle")}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branchPacks.map((a) => <Pack key={a.id} a={a} icon={MapPin} />)}
          </div>
        </>
      )}
    </section>
  );
};

export default TopUpShelf;
